import api from './api'

// Validates a scanned QR payload against the real backend, step by step, so the
// UI can show a specific reason instead of a generic "something went wrong".
//
// Steps:
//   1. Company exists / is active           -> GET /company-activation/validate/{company_id}
//   2. Token is valid and belongs to that company -> POST /api-tokens/validate
//   3. Username is a real, active user of that company -> answered by the same POST /api-tokens/validate
//      (the request carries the username; the response has user_found / user_active)
//
// Throws an Error with a `.type` field the UI can map to copy/iconography.
export const validateQrLogin = async ({ company_id, username, token }) => {
  if (!company_id || !token) {
    const err = new Error('QR Code incompleto — faltam dados da empresa')
    err.type = 'malformed'
    throw err
  }

  // 1. Company
  let company
  try {
    const res = await api.get(`/company-activation/validate/${company_id}`)
    company = res.data
  } catch (error) {
    const status = error.response?.status
    if (status === 400) {
      const err = new Error('ID da empresa inválido')
      err.type = 'invalid_company'
      throw err
    }
    if (status === 404) {
      const err = new Error('Empresa não encontrada')
      err.type = 'invalid_company'
      throw err
    }
    if (status === 403) {
      const err = new Error('Empresa inativa')
      err.type = 'inactive_company'
      throw err
    }
    throw error // network/timeout/server — let classifyApiError handle it upstream
  }

  // 2. Token
  const tokenRes = await api.post('/api-tokens/validate', username ? { token, username } : { token })
  const tokenData = tokenRes.data
  if (!tokenData.valid) {
    const err = new Error('Token inválido ou expirado')
    err.type = 'invalid_token'
    throw err
  }
  if (String(tokenData.company_id) !== String(company_id)) {
    const err = new Error('Este token não pertence a esta empresa')
    err.type = 'token_mismatch'
    throw err
  }

  // 3. Username — answered by the validate call above. Best-effort by design: an older backend that does not
  // know the field leaves user_found undefined, and the step is simply skipped rather than blocking login.
  if (username) {
    if (tokenData.user_found === false) {
      const err = new Error('Usuário não pertence a esta empresa')
      err.type = 'invalid_user'
      throw err
    }
    if (tokenData.user_found === true && tokenData.user_active === false) {
      const err = new Error('Usuário inativo')
      err.type = 'inactive_user'
      throw err
    }
  }

  return { companyName: company.company_name }
}
